import {
    NovaCommanding,
    EntityCommand,
    EntityType,
    EntityStateTransition,
} from '@office-start/nova-facade-types';
import { Module } from 'owa-workloads';
import { onModuleClick, onOfficeAppClick, isModuleSwitchEnabled } from 'owa-left-rail-utils';
import type { OfficeApp } from 'owa-left-rail-utils/lib/store/schema/OfficeApp';
import isSupportedOfficeRailApp from 'owa-left-rail-utils/lib/isSupportedOfficeRailApp';
import NavBarLinkData from 'owa-service/lib/contract/NavBarLinkData';
import { getNavBarLinkData } from 'owa-left-rail-utils/lib/utils/getNavBarLinkData';
import { getBposNavBarData } from 'owa-bpos-store';
import getModuleSwitcherItemUrl from 'owa-left-rail-utils/lib/getModuleSwitcherItemUrl';
import getAppHostAppPath from 'owa-left-rail-utils/lib/getAppHostAppPath';
import { setSelectedApp, getSelectedApp } from 'owa-appbar-state';
import onAppDrawerAppClick from 'owa-app-drawer/lib/actions/onAppDrawerAppClick';

export const novaCommanding: NovaCommanding = {
    trigger(command: EntityCommand): Promise<void> {
        const isRightClick = command.command.stateTransition === EntityStateTransition.new;
        const handler = isRightClick ? rightClickCommandHandling : leftClickCommandHandling;
        handler(command);
        return Promise.resolve();
    },
};

const Office365Apps = ['Word', 'Excel', 'Powerpoint', 'OneNote'];

/**
 * Side-effects of a right mouse click and 'new tab' option on a launch tile
 * @param command
 */
function rightClickCommandHandling(command: EntityCommand): void {
    const appId = command.entity.entityId;
    const url =
        command.entity.type === EntityType.m365_app
            ? getAppUrl(appId)
            : getModuleSwitcherItemUrl(getModuleFromEntityType(command.entity.entityId));

    window.open(url, '_blank');
}

/**
 * Side-effects of a left mouse click on a launch tile
 * @param command
 */
function leftClickCommandHandling(command: EntityCommand): void {
    const appId = (command.entity.entityId || '') as OfficeApp;
    const isApp1stParty = isSupportedOfficeRailApp(appId);
    const isAppOffice365 = Office365Apps.indexOf(appId) !== -1;

    switch (command.entity.type) {
        case EntityType.m365_app:
            if (isApp1stParty) {
                // It's a M365 app that is a 1st party app.
                setSelectedApp(appId);
                onOfficeAppClick(appId);
            } else if (isAppOffice365) {
                // It's an office M365App
                window.open(getAppUrl(appId));
            } else {
                // It's a MetaOS app
                setSelectedApp(appId);
                onAppDrawerAppClick(appId);
            }
            break;
        default:
            // It's an OWA App
            const currentlySelectedModule = getModuleFromEntityType(getSelectedApp());
            const newModule = getModuleFromEntityType(appId);
            if (isModuleSwitchEnabled(currentlySelectedModule, newModule)) {
                setSelectedApp(newModule.toString());
                onModuleClick(newModule, currentlySelectedModule);
            } else {
                // Module switch might not be enabled
                window.open(getModuleSwitcherItemUrl(newModule), '_blank');
            }
    }
}

function getAppUrl(appId: string): string {
    const navBarData = getBposNavBarData();
    if (!navBarData) {
        return '';
    }
    const navBarLinkData: NavBarLinkData = getNavBarLinkData(appId, navBarData);
    return isSupportedOfficeRailApp(appId) ? getAppHostAppPath(appId) : navBarLinkData.Url;
}

function getModuleFromEntityType(id: string): Module {
    let translatedModule: Module;
    switch (id) {
        case 'Mail':
            translatedModule = Module.Mail;
            break;
        case 'Calendar':
            translatedModule = Module.Calendar;
            break;
        case 'People':
            translatedModule = Module.People;
            break;
        case 'FilesHub':
            translatedModule = Module.FilesHub;
            break;
        case 'Tasks':
        case 'ToDo':
            translatedModule = Module.Tasks;
            break;
        case 'Eventify':
            translatedModule = Module.Eventify;
            break;
        default:
            translatedModule = Module.AppHost;
            break;
    }

    return translatedModule;
}
